import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip,
  useToast,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { FiFile, FiTrash2, FiArrowLeft, FiTag, FiFolder, FiFileText } from 'react-icons/fi'
import { FileItem } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'
import apiService from '@/services/api'
import { containerVariants, listItemVariants } from '@/theme/animations'
import { MotionBox } from '@/components/Common/MotionBox'

const MotionTr = motion(Tr as any)

interface TrashExplorerProps {
  onBack: () => void
}

export default function TrashExplorer({ onBack }: TrashExplorerProps) {
  const navigate = useNavigate()
  const { t, formatFileSize: formatFileSizeI18n } = useAppTranslation(['files', 'common'])
  const [trashedFiles, setTrashedFiles] = useState<FileItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()

  const loadTrashedFiles = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const files = await apiService.getTrashItems()
      setTrashedFiles(files)
    } catch (err: any) {
      console.error('Failed to load trashed files:', err)
      setError(t('trash.loadError', { ns: 'files' }))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadTrashedFiles()
  }, [])

  // Calculate total pages from trashed files
  const totalTrashedPages = trashedFiles.reduce((total, file) => {
    const pageCount = file.metadata?.ocr?.page_count || 0
    return total + pageCount
  }, 0)

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <FiFile size="16px" color="#e53e3e" />
    }
    return <FiFile size="16px" />
  }

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDeletedDate = (deletedAt: string) => {
    const date = new Date(deletedAt)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`

    return date.toLocaleDateString()
  }

  if (isLoading) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text color="gray.600">{t('trash.loading', { ns: 'files' })}</Text>
          </VStack>
        </Center>
      </MotionBox>
    )
  }

  if (error) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <Alert status="error" borderRadius="lg" maxW="md">
            <AlertIcon />
            <VStack spacing={2} align="start">
              <Text fontWeight="medium">{t('loadingFiles', { ns: 'errors' })}</Text>
              <Text fontSize="sm">{error}</Text>
            </VStack>
          </Alert>
        </Center>
      </MotionBox>
    )
  }

  return (
    <MotionBox
      initial="initial"
      animate="animate"
      variants={containerVariants}
      p={6}
      h="full"
      overflow="auto"
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              aria-label={t('buttons.back', { ns: 'common' })}
              icon={<FiArrowLeft />}
              size="sm"
              variant="ghost"
              onClick={onBack}
            />
            <HStack spacing={2}>
              <FiTrash2 size="20px" color="#E53E3E" />
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                {t('navigation.trash', { ns: 'common' })}
              </Text>
            </HStack>
          </HStack>

          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              {t('plurals.file', { ns: 'common', count: trashedFiles.length })}
            </Text>
          </HStack>
        </HStack>

        {/* Total Pages Widget */}
        {trashedFiles.length > 0 && (
          <Card>
            <CardBody>
              <Stat>
                <StatLabel color="gray.600" display="flex" alignItems="center" gap={2}>
                  <FiFileText />
                  {t('trash.totalTrashedPages', { ns: 'files' })}
                </StatLabel>
                <StatNumber color="orange.600" fontSize="2xl">
                  {totalTrashedPages.toLocaleString()}
                </StatNumber>
                <StatHelpText color="gray.500">
                  {t('trash.pagesFromDeletedFiles', { ns: 'files' })}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        )}

        {/* Alert about permanent deletion and page usage */}
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <VStack spacing={2} align="start" fontSize="sm">
            <Text fontWeight="medium">{t('trash.permanentDeletionNotice', { ns: 'files' })}</Text>
            <Text>{t('trash.pageUsageNotice', { ns: 'files' })}</Text>
          </VStack>
        </Alert>

        {/* Files Table */}
        {trashedFiles.length === 0 ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Box color="gray.400">
                <FiTrash2 size="48px" />
              </Box>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('trash.emptyTitle', { ns: 'files' })}
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {t('trash.emptyDescription', { ns: 'files' })}
                </Text>
              </VStack>
            </VStack>
          </Center>
        ) : (
          <MotionBox variants={listItemVariants}>
            <TableContainer
              bg="white"
              borderRadius="2xl"
              border="1px"
              borderColor="gray.200"
              boxShadow="sm"
              overflow="hidden"
            >
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th
                      color="gray.700"
                      fontWeight="semibold"
                      fontSize="sm"
                      letterSpacing="wide"
                      borderColor="gray.200"
                      py={3}
                    >
                      {t('labels.name', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th
                      color="gray.700"
                      fontWeight="semibold"
                      fontSize="sm"
                      letterSpacing="wide"
                      borderColor="gray.200"
                      py={3}
                    >
                      {t('labels.folder', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th
                      color="gray.700"
                      fontWeight="semibold"
                      fontSize="sm"
                      letterSpacing="wide"
                      borderColor="gray.200"
                      py={3}
                    >
                      {t('labels.pages', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th
                      color="gray.700"
                      fontWeight="semibold"
                      fontSize="sm"
                      letterSpacing="wide"
                      borderColor="gray.200"
                      py={3}
                    >
                      {t('labels.size', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th
                      color="gray.700"
                      fontWeight="semibold"
                      fontSize="sm"
                      letterSpacing="wide"
                      borderColor="gray.200"
                      py={3}
                    >
                      {t('trash.deleted', { ns: 'files' }).toUpperCase()}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {trashedFiles.map((file, index) => (
                    <MotionTr
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      role="group"
                    >
                      <Td borderColor="gray.200" py={3}>
                        <HStack spacing={3}>
                          <Box color="gray.600">{getFileIcon(file.mime_type)}</Box>
                          <VStack spacing={0} align="start">
                            <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
                              {file.display_name || file.original_name}
                            </Text>
                            {file.tags && file.tags.length > 0 && (
                              <HStack spacing={1}>
                                <FiTag size="10px" color="#9CA3AF" />
                                <Text fontSize="xs" color="gray.500">
                                  {file.tags
                                    .slice(0, 2)
                                    .map((tag) => tag.tag)
                                    .join(', ')}
                                  {file.tags.length > 2 && ` +${file.tags.length - 2}`}
                                </Text>
                              </HStack>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        {file.folder_with_trashed ? (
                          <HStack spacing={2}>
                            <FiFolder
                              size="14px"
                              color={file.folder_with_trashed.deleted_at ? '#E53E3E' : '#9CA3AF'}
                            />
                            <Text
                              fontSize="sm"
                              color={file.folder_with_trashed.deleted_at ? 'red.500' : 'gray.600'}
                              textDecoration={
                                file.folder_with_trashed.deleted_at ? 'line-through' : 'none'
                              }
                              noOfLines={1}
                            >
                              {file.folder_with_trashed.name}
                            </Text>
                            {file.folder_with_trashed.deleted_at && (
                              <Text fontSize="xs" color="red.500" fontStyle="italic">
                                ({t('trash.deletedIndicator', { ns: 'files' })})
                              </Text>
                            )}
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500" fontStyle="italic">
                            {t('labels.root', { ns: 'common' })}
                          </Text>
                        )}
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        <Text fontSize="sm" color="gray.600">
                          {file.metadata?.ocr?.page_count || 0}
                        </Text>
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        <Text fontSize="sm" color="gray.600">
                          {formatFileSize(file.file_size)}
                        </Text>
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        <Text fontSize="sm" color="gray.600">
                          {file.deleted_at && formatDeletedDate(file.deleted_at)}
                        </Text>
                      </Td>
                    </MotionTr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </MotionBox>
        )}
      </VStack>
    </MotionBox>
  )
}
